import React, { useState, useEffect, useRef } from 'react';
import type { Scene } from '../types/scene';
import { sceneService } from '../services/sceneService';
import './SceneImageDisplay.css';

interface SceneImageDisplayProps {
  scene: Scene | null;
  fadeMs?: number;
  holdMs?: number;
}

interface ImageState {
  imagePath: string;
  opacity: number;
  isActive: boolean;
  layer: 'bottom' | 'top';
  isFading: boolean;
}

const SceneImageDisplay: React.FC<SceneImageDisplayProps> = ({
  scene,
  fadeMs = 800,
  holdMs = 2000,
}) => {
  const [imageStates, setImageStates] = useState<ImageState[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [, setBottomImageIndex] = useState(0);
  const [, setTopImageIndex] = useState(-1);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const fadeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!scene || !scene.images || scene.images.length === 0) {
      setImageStates([]);
      return;
    }

    const newImageStates: ImageState[] = scene.images.map((imageName, index) => ({
      imagePath: sceneService.getImagePath(imageName),
      opacity: index === 0 ? 1 : 0,
      isActive: index === 0,
      layer: index === 0 ? 'bottom' : 'top',
      isFading: false,
    }));

    setImageStates(newImageStates);
    setCurrentImageIndex(0);
    setBottomImageIndex(0);
    setTopImageIndex(-1);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current);
    }

    if (scene.images.length > 1) {
      const effectiveFadeMs = scene.fadeMs || fadeMs;
      const effectiveHoldMs = scene.holdMs || holdMs;

      const startImageCycle = () => {
        let currentIndex = 0;

        intervalRef.current = setInterval(() => {
          const nextIndex = (currentIndex + 1) % scene.images.length;
          
          console.log(`画像切り替え: ${currentIndex} → ${nextIndex}`);
          
          // 新しい画像をトップレイヤーに透明状態で配置
          setTopImageIndex(nextIndex);
          setImageStates(prev => 
            prev.map((state, index) => {
              if (index === nextIndex) {
                // 新しい画像: トップレイヤーに透明で配置
                return {
                  ...state,
                  opacity: 0,
                  isActive: true,
                  layer: 'top',
                  isFading: true,
                };
              } else if (index === currentIndex) {
                // 現在の画像: ボトムレイヤーで表示継続（削除しない）
                return {
                  ...state,
                  opacity: 1,
                  isActive: true,
                  layer: 'bottom',
                  isFading: false,
                };
              } else {
                // その他の画像: 現在の状態を維持
                return state;
              }
            })
          );
          
          // フェードイン開始（次のフレームで）
          setTimeout(() => {
            setImageStates(prev => 
              prev.map((state, index) => {
                if (index === nextIndex) {
                  // 新しい画像: フェードイン
                  return { ...state, opacity: 1 };
                }
                return state; // その他は変更なし（前の画像も残る）
              })
            );
            
            // フェード完了後に新しい画像をボトムレイヤーに移動
            fadeTimeoutRef.current = setTimeout(() => {
              setBottomImageIndex(nextIndex);
              setTopImageIndex(-1);
              setImageStates(prev => 
                prev.map((state, index) => {
                  if (index === nextIndex) {
                    // 新しい画像: ボトムレイヤーに移動
                    return {
                      ...state,
                      opacity: 1,
                      isActive: true,
                      layer: 'bottom',
                      isFading: false,
                    };
                  } else if (index === currentIndex) {
                    // 前の画像: 非表示にする
                    return {
                      ...state,
                      opacity: 0,
                      isActive: false,
                      layer: 'top',
                      isFading: false,
                    };
                  } else {
                    // その他: 非表示
                    return {
                      ...state,
                      opacity: 0,
                      isActive: false,
                      layer: 'top',
                      isFading: false,
                    };
                  }
                })
              );
              console.log(`フェード完了: 画像${nextIndex}がボトムレイヤーに移動`);
            }, effectiveFadeMs);
          }, 50);
          
          setCurrentImageIndex(nextIndex);
          currentIndex = nextIndex;
        }, effectiveHoldMs + effectiveFadeMs);
      };

      timeoutRef.current = setTimeout(startImageCycle, effectiveHoldMs);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
      }
    };
  }, [scene, fadeMs, holdMs]);

  if (!scene || imageStates.length === 0) {
    return (
      <div className="scene-image-container">
        <div className="scene-image-placeholder">
          <span>シーン画像を読み込み中...</span>
        </div>
      </div>
    );
  }

  const effectiveFadeMs = scene.fadeMs || fadeMs;

  return (
    <div className="scene-image-container">
      {/* Bottom layer - current image */}
      {imageStates.map((imageState, index) => {
        if (imageState.layer === 'bottom' && imageState.isActive) {
          return (
            <img
              key={`bottom-${imageState.imagePath}`}
              src={imageState.imagePath}
              alt={`${scene.title} - Image ${index + 1}`}
              className="scene-image scene-image-bottom"
              style={{
                opacity: 1,
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                zIndex: 1,
              }}
              onError={(e) => {
                console.warn(`Failed to load image: ${imageState.imagePath}`);
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          );
        }
        return null;
      })}
      
      {/* Top layer - fading in image */}
      {imageStates.map((imageState, index) => {
        if (imageState.layer === 'top' && imageState.isFading) {
          return (
            <img
              key={`top-${imageState.imagePath}`}
              src={imageState.imagePath}
              alt={`${scene.title} - Image ${index + 1}`}
              className="scene-image scene-image-top"
              style={{
                opacity: imageState.opacity,
                transition: `opacity ${effectiveFadeMs}ms ease-in-out`,
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                zIndex: 2,
              }}
              onError={(e) => {
                console.warn(`Failed to load image: ${imageState.imagePath}`);
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          );
        }
        return null;
      })}
      
      {imageStates.length > 1 && (
        <div className="image-indicators">
          {imageStates.map((_, index) => (
            <div
              key={index}
              className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SceneImageDisplay;